import type { RouterInputs } from '@shared/all/domains/trpc';
import { useState } from 'react';
import { createPasskey, getPasskey, isPasskeySupported, passkeyAutofillAvailable } from '../helpers/passkeyHelper';
import { PasskeyCancelledError } from '../helpers/passkeyHelper.types';
import toast from '../helpers/toast';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
type RegisterMeta = RouterInputs['auth']['passkey']['registerVerify'];

// Which app the ceremony is running in, so the session it mints is named after
// it. Taken off the router input rather than restated, so adding a client on the
// server cannot leave this behind.
type ClientApp = NonNullable<RouterInputs['auth']['login']['app']>;
export type PasskeySignUpArgs = {
  username: string;
  instanceId: number;
  referrerUserId?: number;
  types?: RegisterMeta['types'];
  app?: ClientApp;
};
export type PasskeySignInArgs = {
  instanceId: number;
  useAutofill?: boolean;
  app?: ClientApp;
};

// Shared passkey orchestration for web + mobile. Returns booleans so each
// platform runs its own post-auth handoff (initializeUser + navigation).
const usePasskey = (client: WrappedTRPCClient) => {
  const [busy, setBusy] = useState(false);
  const handleFailure = (message: string, error: unknown, silent = false): boolean => {
    // Quiet no-op when the user dismisses the OS passkey sheet, or when the
    // caller never asked for this attempt in the first place.
    if (silent || error instanceof PasskeyCancelledError) return false;
    toast(message, undefined, 'error');
    return false;
  };
  const signUpWithPasskey = async ({
    username,
    instanceId,
    referrerUserId,
    types,
    app
  }: PasskeySignUpArgs): Promise<boolean> => {
    setBusy(true);
    try {
      const [optErr, opt] = await client.auth.passkey.registerOptions.mutate({
        username
      });
      if (optErr || !opt) {
        toast('Could not start passkey signup.', undefined, 'error');
        return false;
      }
      const response = await createPasskey(opt.options);

      // No username: the server uses the one bound to the challenge, so the
      // account matches what the authenticator saved.
      const [verErr] = await client.auth.passkey.registerVerify.mutate({
        flowId: opt.flowId,
        response,
        instanceId,
        referrerUserId,
        types,
        app
      });
      if (verErr) {
        toast(verErr.message || 'Passkey signup failed.', undefined, 'error');
        return false;
      }
      return true;
    } catch (error_0) {
      return handleFailure('Passkey signup failed.', error_0);
    } finally {
      setBusy(false);
    }
  };

  // Add a passkey to the ALREADY signed-in account (Settings). Unlike
  // signUpWithPasskey (which creates a new account), this hits factiii's
  // add-to-existing procedures. Returns true on success so the caller refetches.
  const addPasskey = async (name?: string): Promise<boolean> => {
    setBusy(true);
    try {
      const [optErr_0, opt_0] = await client.auth.passkey.addOptions.mutate();
      if (optErr_0 || !opt_0) {
        toast('Could not start adding a passkey.', undefined, 'error');
        return false;
      }
      const response_0 = await createPasskey(opt_0.options);
      const [verErr_0] = await client.auth.passkey.addVerify.mutate({
        flowId: opt_0.flowId,
        response: response_0,
        name
      });
      if (verErr_0) {
        toast(verErr_0.message || 'Could not add passkey.', undefined, 'error');
        return false;
      }
      return true;
    } catch (error_1) {
      return handleFailure('Could not add passkey.', error_1);
    } finally {
      setBusy(false);
    }
  };
  const signInWithPasskey = async ({
    instanceId: instanceId_0,
    useAutofill = false,
    app: app_0
  }: PasskeySignInArgs): Promise<boolean> => {
    // Autofill stays pending until the user picks from the browser dropdown, or
    // forever, so only explicit taps count as busy.
    if (!useAutofill) setBusy(true);
    try {
      // Sign-in is fully discoverable: the authenticator offers the saved
      // passkeys, so there's no username to send.
      const [optErr_1, opt_1] = await client.auth.passkey.authOptions.mutate();
      if (optErr_1 || !opt_1) {
        if (!useAutofill) {
          toast('Could not start passkey sign-in.', undefined, 'error');
        }
        return false;
      }
      const response_1 = await getPasskey(opt_1.options, useAutofill);
      const [verErr_1] = await client.auth.passkey.authVerify.mutate({
        flowId: opt_1.flowId,
        instanceId: instanceId_0,
        response: response_1,
        app: app_0
      });
      if (verErr_1) {
        if (!useAutofill) {
          toast(verErr_1.message || 'Passkey sign-in failed.', undefined, 'error');
        }
        return false;
      }
      return true;
    } catch (error_2) {
      // Autofill is a background offer the login screen makes on mount, not
      // something anyone asked for, so it must fail in silence. It used to toast:
      // every visit to /login greeted the user with "Passkey sign-in failed."
      // before they had touched anything, on any browser or device where
      // conditional UI starts and cannot finish.
      return handleFailure('Passkey sign-in failed.', error_2, useAutofill);
    } finally {
      if (!useAutofill) setBusy(false);
    }
  };
  return {
    busy,
    signUpWithPasskey,
    signInWithPasskey,
    addPasskey,
    isPasskeySupported,
    passkeyAutofillAvailable
  };
};
export default usePasskey;